import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { Target } from "lucide-react";
import { StreamGoalsDialog } from "@/components/StreamGoalsDialog";

export const STREAM_COLORS = [
  "#1A362D", "#C85A3C", "#557C55", "#D4A24C", "#2C6E49", "#7B5E3B",
  "#4A6FA5", "#8C4A6B", "#4A4D4A", "#9DB17C", "#B8735A", "#3E7C7C",
];
export const streamColor = (key = "") => {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return STREAM_COLORS[h % STREAM_COLORS.length];
};
const MISS = "#C0392B";

const isHit = (v, t) => (t.direction === "min" ? v >= t.target_kg : v <= t.target_kg);

const GoalDot = ({ cx, cy, value, color, target }) => {
  if (cx == null || cy == null) return null;
  if (!target) return <circle cx={cx} cy={cy} r={3} fill={color} />;
  const hit = isHit(value, target);
  return hit
    ? <circle cx={cx} cy={cy} r={4.5} fill={color} stroke="#fff" strokeWidth={1.5} />
    : <circle cx={cx} cy={cy} r={4.5} fill="#fff" stroke={MISS} strokeWidth={2} />;
};

const GoalSummary = ({ streams, data, targetMap }) => {
  const withGoals = streams.filter((s) => targetMap[s.key]);
  if (!withGoals.length || !data.length) return null;
  return (
    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="goal-summary">
      {withGoals.map((s) => {
        const t = targetMap[s.key];
        const vals = data.map((d) => d.streams?.[s.key] ?? 0);
        const hits = vals.filter((v) => isHit(v, t)).length;
        const latest = vals[vals.length - 1];
        const ok = isHit(latest, t);
        return (
          <div key={s.key} className="rounded-md border px-3 py-2 text-xs" style={{ borderColor: "rgba(10,10,10,0.1)" }} data-testid={`goal-status-${s.key}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="truncate font-medium" title={s.name}>{s.name}</span>
              <span className="pill shrink-0" style={{ background: ok ? "rgba(26,54,45,0.1)" : "rgba(192,57,43,0.1)", color: ok ? "var(--forest)" : MISS }}>
                {ok ? "On target" : "Missed"}
              </span>
            </div>
            <div className="text-neutral-500 mt-1">
              {t.direction === "min" ? "At least" : "At most"} {t.target_kg} kg/month · hit {hits} of {vals.length} months
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const StreamTrendChart = ({ streams = [], data = [], periodLabel, targets = [], canEdit = false, clientId, onTargetsSaved }) => {
  const [hidden, setHidden] = useState({});
  const [editing, setEditing] = useState(false);
  const toggle = (key) => setHidden((h) => ({ ...h, [key]: !h[key] }));
  const targetMap = Object.fromEntries(targets.map((t) => [t.stream_key, t]));

  return (
    <div className="hbt-card p-5" data-testid="stream-trend-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="hbt-label">Stream trend</div>
          <div className="font-display text-lg font-semibold">Each waste stream, month by month</div>
          <div className="text-xs text-neutral-500 mt-1">
            Tap a stream below to hide or show its line.{targets.length > 0 && " Dashed lines are monthly goals; hollow red dots are missed months."}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-xs text-neutral-500">{periodLabel}</div>
          {canEdit && streams.length > 0 && (
            <button type="button" onClick={() => setEditing(true)} className="btn-outline inline-flex items-center gap-2 text-xs" data-testid="set-stream-goals-btn">
              <Target className="w-3.5 h-3.5" /> {targets.length ? "Edit goals" : "Set goals"}
            </button>
          )}
        </div>
      </div>
      {data.length === 0 || streams.length === 0 ? (
        <div className="text-sm text-neutral-400 py-10 text-center" data-testid="stream-trend-empty">No collections in this period.</div>
      ) : (
        <>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(10,10,10,0.08)" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit=" kg" width={64} />
                <Tooltip
                  contentStyle={{ borderRadius: 6, border: "1px solid rgba(10,10,10,0.15)", fontSize: 12 }}
                  formatter={(v) => `${Number(v).toFixed(1)} kg`}
                />
                {streams.map((s, i) => {
                  const t = targetMap[s.key];
                  return t && !hidden[s.key] ? (
                    <ReferenceLine key={`goal-${s.key}`} y={t.target_kg} stroke={streamColor(s.key)} strokeDasharray="6 4" strokeOpacity={0.7} ifOverflow="extendDomain" />
                  ) : null;
                })}
                {streams.map((s, i) => {
                  const color = streamColor(s.key);
                  return (
                    <Line
                      key={s.key}
                      type="monotone"
                      name={s.name}
                      dataKey={(d) => d.streams?.[s.key] ?? 0}
                      stroke={color}
                      strokeWidth={2}
                      dot={(p) => <GoalDot key={`${s.key}-${p.index}`} {...p} color={color} target={targetMap[s.key]} />}
                      activeDot={{ r: 5 }}
                      hide={!!hidden[s.key]}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {streams.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={() => toggle(s.key)}
                data-testid={`stream-trend-toggle-${s.key}`}
                className={`flex items-center gap-2 text-xs px-2.5 py-1 rounded-full border transition-opacity duration-200 hover:border-neutral-400 ${hidden[s.key] ? "opacity-40" : "opacity-100"}`}
                style={{ borderColor: "rgba(10,10,10,0.12)" }}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: streamColor(s.key) }} />
                {s.name}
                {targetMap[s.key] && <Target className="w-3 h-3 text-neutral-500" />}
              </button>
            ))}
          </div>
          <GoalSummary streams={streams} data={data} targetMap={targetMap} />
        </>
      )}
      {canEdit && (
        <StreamGoalsDialog open={editing} onOpenChange={setEditing} clientId={clientId} streams={streams} targets={targets} onSaved={onTargetsSaved} />
      )}
    </div>
  );
};
